import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RequireRole } from '../components/auth/RequireRole';
import { UserRole } from '../lib/types/auth';
import FinChatsPanel from '../components/finChats/finChatsPanel';
import ChatThread from '../components/finChats/finChatsThread';
import { MessageCircle, Search, Plus, X, User, Check, CheckCheck } from 'lucide-react';
import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { toast } from 'react-hot-toast';
import { cn } from '../lib/utils';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useAuth } from '../context/AuthContext';

import UserSelector from '../components/task/UserSelector';
import { chatNotificationService } from '../services/chatNotificationService';

// Keep this list in sync with hasFinChatsRole() in firestore.rules — otherwise
// a role the backend allows (e.g. Owner / IM / Marketing Rep) still gets an
// "Access Denied" screen from the UI gate below.
const ALLOWED_ROLES = [
  UserRole.MasterAdmin,
  UserRole.CSAdmin,
  UserRole.MarketingAdmin,
  'Marketing Rep' as UserRole,
  UserRole.Template,
  UserRole.TemplateAdmin,
  UserRole.WatchAdmin,
  UserRole.Marketing,
  UserRole.SalesRep,
  UserRole.IM,
  UserRole.Owner,
  UserRole.Influencer,
  UserRole.StoreManager,
  UserRole.StoreStaff,
];

const FinChatsPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatEmail, setNewChatEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [shouldCreateChat, setShouldCreateChat] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [selectedUserData, setSelectedUserData] = useState<{
    id: string;
    email: string;
    displayName?: string;
    photoURL?: string;
    roles?: string[];
  } | null>(null);
  // Treat tablets (incl. iPad portrait/landscape up to 1024px) like mobile so the
  // chat uses the full-width list/detail flow instead of a cramped 380px split.
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [showChatList, setShowChatList] = useState(true); // Mobile: show chat list by default
  
  // Split-screen state
  const [isSplitScreenMode, setIsSplitScreenMode] = useState(false);
  const [secondChatId, setSecondChatId] = useState<string | null>(null);

  // Handle split-screen chat selection
  const handleSplitScreenChatSelect = (selectedChatId: string) => {
    if (isSplitScreenMode && chatId && chatId !== selectedChatId) {
      // If in split-screen mode and selecting a different chat, set it as second chat
      setSecondChatId(selectedChatId);
    } else {
      // Normal navigation
      handleMobileChatSelect(selectedChatId);
    }
  };

  // Handle mobile chat selection
  const handleMobileChatSelect = (selectedChatId: string) => {
    if (isMobile) {
      setShowChatList(false); // Hide chat list on mobile when chat is selected
    }
    // Navigate to the selected chat using React Router
    navigate(`/chats/${selectedChatId}`);
  };

  // Handle mobile back to chat list
  const handleBackToChatList = () => {
    if (isMobile) {
      setShowChatList(true);
      // Navigate back to chat list using React Router
      navigate('/chats');
    }
  };

  // Update showChatList when chatId changes or screen size changes
  useEffect(() => {
    if (isMobile) {
      setShowChatList(!chatId); // Show list if no chat selected, hide if chat selected
    } else {
      setShowChatList(true); // Always show on desktop
    }
  }, [chatId, isMobile]);

  // Mark all chats as read
  const handleMarkAllAsRead = async () => {
    if (!currentUser?.email) return;
    
    try {
      await chatNotificationService.markAllChatsAsRead(currentUser.email);
      toast.success('All chats marked as read');
    } catch (error) {
      console.error('Error marking all chats as read:', error);
      toast.error('Failed to mark all chats as read');
    }
  };

  // Debug logging
  console.log('Modal state:', { showNewChatModal, newChatEmail, isValidating });
  
  // Add authentication debugging
  useEffect(() => {
    console.log('Current user auth state:', {
      isAuthenticated: !!currentUser,
      email: currentUser?.email,
      uid: currentUser?.uid,
      // Don't log full user object as it might be large
    });
  }, [currentUser]);

  const handleUserSelect = (userId: string | null, userData: { id: string; email: string; displayName?: string; photoURL?: string; roles?: string[] } | null) => {
    setSelectedUserId(userId);
    setSelectedUserData(userData);
  };

  // Load all users when modal opens
  const loadAllUsers = async () => {
    if (allUsers.length > 0) return; // Already loaded
    
    setIsValidating(true);
    try {
      console.log('Loading all users for chat selection...');
      
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const users = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => user.email !== currentUser?.email) // Only exclude current user
        .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email));
      
      console.log(`Loaded ${users.length} total users`);
      setAllUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsValidating(false);
    }
  };

  // Filter users based on search input
  const filterUsers = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredUsers(allUsers);
      return;
    }
    
    const filtered = allUsers.filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.displayName && user.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setFilteredUsers(filtered);
  };

  const searchForUser = async () => {
    if (!newChatEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setIsValidating(true);
    setSearchResults([]);

    try {
      console.log('Searching for user:', newChatEmail.trim().toLowerCase());
      
      // Check if user exists and has appropriate role
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', newChatEmail.trim().toLowerCase()));
      const querySnapshot = await getDocs(q);

      console.log('Query result:', querySnapshot.size, 'documents found');

      if (querySnapshot.empty) {
        toast.error(`User "${newChatEmail.trim()}" not found in the system`);
        setIsValidating(false);
        return;
      }

      const userData = querySnapshot.docs[0].data();
      const userRoles = userData.roles || [];
      
      console.log('User data found:', { email: userData.email, roles: userRoles });

      // Check if user has any of the allowed roles
      const hasValidRole = userRoles.some((role: string) => ALLOWED_ROLES.includes(role as UserRole));

      console.log('Role validation:', { userRoles, allowedRoles: ALLOWED_ROLES, hasValidRole });

      if (!hasValidRole) {
        toast.error(`User "${newChatEmail.trim()}" does not have permission to access chats`);
        setIsValidating(false);
        return;
      }

      // Add user to search results
      setSearchResults([{
        email: userData.email,
        displayName: userData.displayName || userData.email,
        photoURL: userData.photoURL,
        roles: userRoles
      }]);
      
      toast.success('User found!');
    } catch (error) {
      console.error('Error searching for user:', error);
      toast.error(`Failed to search for user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsValidating(false);
    }
  };

  const addUserToChat = (email: string) => {
    if (!selectedUsers.includes(email) && email !== currentUser?.email) {
      setSelectedUsers([...selectedUsers, email]);
      setNewChatEmail('');
      setSearchResults([]);
      setFilteredUsers(allUsers); // Reset filter
    }
  };

  const addUserFromList = (user: any) => {
    if (!selectedUsers.includes(user.email) && user.email !== currentUser?.email) {
      setSelectedUsers([...selectedUsers, user.email]);
      setNewChatEmail('');
      setFilteredUsers(allUsers); // Reset filter
    }
  };

  const removeUserFromChat = (email: string) => {
    setSelectedUsers(selectedUsers.filter(u => u !== email));
  };

  const createChat = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user to chat with');
      return;
    }

    setIsCreatingChat(true);
    try {
      const allParticipants = [currentUser?.email, ...selectedUsers].filter(Boolean);
      
      if (selectedUsers.length === 1) {
        // Single user chat - use existing logic
        const targetUser = selectedUsers[0];
        setShowNewChatModal(false);
        setSelectedUsers([]);
        setSearchResults([]);
        setShouldCreateChat(true);
        setNewChatEmail(targetUser);
        
        setTimeout(() => {
          setShouldCreateChat(false);
        }, 1000);
        
        toast.success('Creating chat...');
      } else {
        // Group chat - create directly here
        await createGroupChat(allParticipants, selectedUsers);
      }
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error('Failed to create chat');
    } finally {
      setIsCreatingChat(false);
    }
  };

  const createGroupChat = async (allParticipants: string[], selectedUsers: string[]) => {
    try {
      // Check if a group chat with these exact participants already exists
      const existingGroupChat = await checkForExistingGroupChat(allParticipants);
      
      if (existingGroupChat) {
        navigate(`/chats/${existingGroupChat.id}`);
        toast.success('Opened existing group chat');
        setShowNewChatModal(false);
        setSelectedUsers([]);
        setSearchResults([]);
        return;
      }

      // Create new group chat
      const groupChatData = {
        participants: allParticipants.map(email => email.toLowerCase()),
        createdBy: currentUser?.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        lastMessage: '',
        isPrivate: false, // Group chats are not private
        name: `Group Chat (${selectedUsers.length} members)` // Default name
      };

      const docRef = await addDoc(collection(db, 'chats'), groupChatData);
      
      // Navigate to the new group chat
      navigate(`/chats/${docRef.id}`);
      toast.success(`Group chat created with ${selectedUsers.length} users!`);
      
      setShowNewChatModal(false);
      setSelectedUsers([]);
      setSearchResults([]);
    } catch (error) {
      console.error('Error creating group chat:', error);
      toast.error('Failed to create group chat');
      throw error;
    }
  };

  const checkForExistingGroupChat = async (participants: string[]): Promise<any | null> => {
    try {
      const normalizedParticipants = participants.map(email => email.toLowerCase()).sort();
      
      // Query for chats where current user is a participant
      const chatsRef = collection(db, 'chats');
      const q = query(chatsRef, where('participants', 'array-contains', currentUser?.email?.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      // Check each chat to see if it has the exact same participants
      for (const doc of querySnapshot.docs) {
        const chatData = doc.data();
        const chatParticipants = chatData.participants?.map((email: string) => email.toLowerCase()).sort();
        
        if (chatParticipants && 
            chatParticipants.length === normalizedParticipants.length &&
            chatParticipants.every((email: string, index: number) => email === normalizedParticipants[index])) {
          return { id: doc.id, ...chatData };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error checking for existing group chat:', error);
      return null;
    }
  };

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES}>
      <div className="relative min-h-screen bg-[#faf9f7] text-gray-900 overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-40 w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl" />
        </div>
        
        {/* Main Content Container */}
        <div className="h-[100dvh] p-3 md:p-6 relative z-10">
          <div className="relative z-10 flex h-full">
          {/* Left sidebar - Mobile: full width when showChatList, hidden when chat selected */}
          <div className={cn(
            "flex flex-col overflow-visible bg-white rounded-2xl shadow-sm border border-gray-200",
            isMobile 
              ? showChatList 
                ? "w-full" 
                : "hidden"
              : "w-[380px] mr-6"
          )}>
          {/* Sidebar header */}
          <div className="p-3 md:p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                    FIN Chats
                  </h1>
                  {selectedUserData && (
                    <p className="text-sm text-gray-500 mt-1">
                      Viewing chats for: <span className="text-teal-600 font-medium">{selectedUserData.displayName || selectedUserData.email}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 md:gap-2">
                {/* Mark All as Read Button */}
                <button
                  onClick={handleMarkAllAsRead}
                  className="relative w-10 h-10 rounded-full transition-all duration-200 group hover:bg-gray-100"
                  title="Mark all chats as read"
                >
                  <div className="relative w-10 h-10 rounded-full flex items-center justify-center">
                    <CheckCheck className="w-4 h-4 text-teal-600" />
                  </div>
                </button>

                {/* User Selector - Master Admin Only */}
                <UserSelector
                  selectedUserId={selectedUserId}
                  onUserSelect={handleUserSelect}
                />
                
                {/* Split-screen toggle - Desktop only */}
                {!isMobile && chatId && (
                  <button
                    onClick={() => {
                      setIsSplitScreenMode(!isSplitScreenMode);
                      if (isSplitScreenMode) {
                        setSecondChatId(null);
                      }
                    }}
                    className={cn(
                      "p-2 rounded-full transition-colors relative",
                      isSplitScreenMode 
                        ? "bg-teal-100 text-teal-600" 
                        : "hover:bg-gray-100 text-gray-500"
                    )}
                    title={isSplitScreenMode ? "Exit split-screen mode" : "Enable split-screen mode"}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="8" height="18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="13" y="3" width="8" height="18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {isSplitScreenMode && secondChatId && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></span>
                    )}
                  </button>
                )}
                
                <button 
                  onClick={() => setShowNewChatModal(true)}
                  disabled={!!selectedUserId}
                  className={cn(
                    "p-2 rounded-full transition-colors",
                    selectedUserId 
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                      : "hover:bg-gray-100 text-teal-600"
                  )}
                  title={selectedUserId ? "Cannot create chats when viewing another user's chats" : "Create new chat"}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-4 py-2 text-sm text-gray-900
                         placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
              />
            </div>
          </div>
          
          {/* Chat list */}
          <div className="flex-1 overflow-y-auto overflow-x-visible custom-scrollbar">
            <FinChatsPanel 
              newChatEmail={newChatEmail} 
              setNewChatEmail={setNewChatEmail} 
              searchQuery={searchQuery}
              shouldCreateChat={shouldCreateChat}
              selectedUserId={selectedUserId}
              onChatSelect={isMobile ? handleMobileChatSelect : (isSplitScreenMode ? handleSplitScreenChatSelect : undefined)}
            />
          </div>
          {isSplitScreenMode && chatId && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gradient-to-r from-teal-50 to-emerald-50">
              <p className="text-xs text-gray-600 text-center">
                <span className="text-teal-600 font-semibold">Split-screen mode active</span> - Click a chat to open it on the right
              </p>
            </div>
          )}
          </div>

          {/* Main chat area - Mobile: full width when chat selected, hidden when chat list shown */}
          {/* Split-screen mode: show two chats side by side */}
          {isSplitScreenMode && !isMobile ? (
            <div className="flex-1 flex gap-4 overflow-hidden">
              {/* First Chat (Left) */}
              <div className={cn(
                "flex flex-col overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-200",
                secondChatId ? "w-1/2" : "flex-1"
              )}>
                {chatId ? (
                  <>
                    <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-emerald-50">
                      <h3 className="text-sm font-semibold text-gray-700">Chat 1</h3>
                      {secondChatId && (
                        <button
                          onClick={() => setSecondChatId(null)}
                          className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                        >
                          Close Right
                        </button>
                      )}
                    </div>
                    <ChatThread 
                      chatId={chatId} 
                      selectedUserId={selectedUserId}
                      isReadOnly={!!selectedUserId}
                    />
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center p-4">
                    <MessageCircle className="w-8 h-8 text-teal-500 mb-4" />
                    <p className="text-gray-500 text-sm">Select a chat from the list</p>
                  </div>
                )}
              </div>

              {/* Second Chat (Right) */}
              {secondChatId && (
                <div className="w-1/2 flex flex-col overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                    <h3 className="text-sm font-semibold text-gray-700">Chat 2</h3>
                    <button
                      onClick={() => setSecondChatId(null)}
                      className="p-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                      title="Close this chat"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <ChatThread 
                    chatId={secondChatId} 
                    selectedUserId={selectedUserId}
                    isReadOnly={!!selectedUserId}
                  />
                </div>
              )}

              {/* Placeholder for second chat */}
              {!secondChatId && (
                <div className="w-1/2 flex flex-col items-center justify-center text-center p-4 bg-white rounded-2xl border-2 border-dashed border-gray-200">
                  <MessageCircle className="w-12 h-12 text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    Select a Second Chat
                  </h3>
                  <p className="text-gray-400 text-sm max-w-xs">
                    Click on another chat from the list to open it here
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Normal single-chat mode */
            <div className={cn(
              "flex flex-col overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-200",
              isMobile 
                ? showChatList 
                  ? "hidden" 
                  : "w-full"
                : "flex-1"
            )}>
              {chatId ? (
                <>
                  {/* Mobile back button */}
                  {isMobile && (
                    <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-gray-50">
                      <button
                        onClick={handleBackToChatList}
                        className="p-2 rounded-full hover:bg-gray-200 text-teal-600 transition-colors"
                        title="Back to chat list"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <h2 className="text-lg font-semibold text-gray-900">Chat</h2>
                    </div>
                  )}
                  <ChatThread 
                    chatId={chatId} 
                    selectedUserId={selectedUserId}
                    isReadOnly={!!selectedUserId}
                  />
                </>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 mb-4 md:mb-6 rounded-full bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 md:w-8 md:h-8 text-teal-600" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                    Welcome to FIN Chats
                  </h2>
                  <p className="text-gray-500 max-w-md text-sm md:text-base">
                    {isMobile ? 'Select a conversation from the list to start messaging.' : 'Select a conversation to start messaging or create a new chat using the plus button.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* New Chat Modal */}
        {showNewChatModal && (
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="rounded-2xl p-6 w-full max-w-[500px] max-h-[80vh] border border-gray-200 bg-white shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">New Chat</h3>
                <button 
                  onClick={() => {
                    console.log('Closing modal and resetting state');
                    setShowNewChatModal(false);
                    setNewChatEmail('');
                    setIsValidating(false);
                    setSearchResults([]);
                    setSelectedUsers([]);
                    setIsCreatingChat(false);
                    setShouldCreateChat(false);
                    setAllUsers([]);
                    setFilteredUsers([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Search for users to start a chat
                  </label>
                  <input
                    type="text"
                    value={newChatEmail}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewChatEmail(value);
                      filterUsers(value);
                    }}
                    onFocus={() => {
                      console.log('Input focused - loading users');
                      loadAllUsers();
                    }}
                    placeholder="Search by name or email..."
                    disabled={isValidating}
                    autoFocus
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900
                             placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* User List */}
                {(filteredUsers.length > 0 || isValidating) && (
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-600">
                      {isValidating ? 'Loading users...' : `Available Users (${filteredUsers.length})`}
                    </label>
                    <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
                      {isValidating ? (
                        <div className="flex items-center justify-center p-4">
                          <div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        filteredUsers.map((user) => (
                          <div 
                            key={user.email} 
                            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                              selectedUsers.includes(user.email)
                                ? 'bg-teal-50 text-teal-700 border-teal-200'
                                : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => addUserFromList(user)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#EFE3C4] flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {user.photoURL ? (
                                  <img 
                                    src={user.photoURL} 
                                    alt={user.displayName || user.email}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  <User className="w-4 h-4 text-[#7A5A34]" />
                                )}
                              </div>
                              <div>
                                <p className="text-gray-900 text-sm font-medium">
                                  {user.displayName || user.email}
                                </p>
                                {user.displayName && (
                                  <p className="text-gray-500 text-xs">{user.email}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {selectedUsers.includes(user.email) ? (
                                <Check className="w-4 h-4 text-teal-600" />
                              ) : (
                                <Plus className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-600">Search Results:</label>
                    {searchResults.map((user) => (
                      <div key={user.email} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <div>
                          <p className="text-gray-900 text-sm">{user.displayName}</p>
                          <p className="text-gray-500 text-xs">{user.email}</p>
                        </div>
                        <button
                          onClick={() => addUserToChat(user.email)}
                          className="px-3 py-1 text-xs bg-teal-500 hover:bg-teal-600 text-white rounded-md transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected Users */}
                {selectedUsers.length > 0 && (
                  <div className="space-y-2">
                    <label className="block text-sm text-gray-600">Selected Users:</label>
                    {selectedUsers.map((email) => (
                      <div key={email} className="flex items-center justify-between p-2 bg-teal-50 rounded-lg border border-teal-200">
                        <p className="text-gray-900 text-sm">{email}</p>
                        <button
                          onClick={() => removeUserFromChat(email)}
                          className="px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={createChat}
                    disabled={selectedUsers.length === 0 || isCreatingChat}
                    className="w-full py-3 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-500
                             text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed
                             hover:shadow-lg hover:shadow-teal-500/20 transition-all"
                  >
                    {isCreatingChat ? 'Creating Chat...' : selectedUsers.length > 0 ? `Start Chat with ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}` : 'Select users to start chat'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </RequireRole>
  );
};

export default FinChatsPage;
